import React, { useEffect, useState } from 'react';
import organizationMembersService, { OrganizationMember } from '../../services/crm/organizationMembersService';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import { useCrmRoleStore, isManager } from '../../store/crmRole.store';

const SettingsMembers: React.FC = () => {
  const { activeOrgId } = useCrmOrgStore();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteUserId, setInviteUserId] = useState('');
  const [inviteRole, setInviteRole] = useState<OrganizationMember['role']>('sales_rep');
  const { role } = useCrmRoleStore();

  useEffect(() => { document.title = 'CRM | Settings'; }, []);

  const loadMembers = async (organization_id: string) => {
    setLoading(true); setError(null);
    try {
      const { data } = await organizationMembersService.list(organization_id);
      setMembers(data || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (activeOrgId) loadMembers(activeOrgId); }, [activeOrgId]);

  const addMember = async () => {
    if (!activeOrgId || !inviteUserId) return;
    setLoading(true); setError(null);
    try {
      await organizationMembersService.add(activeOrgId, inviteUserId, inviteRole);
      setInviteUserId('');
      setInviteRole('sales_rep');
      await loadMembers(activeOrgId);
    } catch (e: any) {
      setError(e?.message || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (id: string) => {
    if (!activeOrgId) return;
    setLoading(true); setError(null);
    try {
      await organizationMembersService.remove(activeOrgId, id);
      await loadMembers(activeOrgId);
    } catch (e: any) {
      setError(e?.message || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  if (!activeOrgId) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">CRM Settings</h1>
        <div className="bg-white p-4 rounded shadow">
          <p className="text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">CRM Settings</h1>

      <div className="bg-white p-4 rounded shadow space-y-4">
        {isManager(role) && (
          <div>
            <h2 className="font-medium mb-2">Invite Member</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input className="border rounded p-2" placeholder="User ID (UUID)" value={inviteUserId} onChange={e => setInviteUserId(e.target.value)} />
              <select className="border rounded p-2" value={inviteRole} onChange={e => setInviteRole(e.target.value as OrganizationMember['role'])}>
                <option value="sales_rep">Sales Rep</option>
              </select>
              <button className="bg-blue-600 text-white rounded px-4" onClick={addMember} disabled={loading}>Add</button>
            </div>
          </div>
        )}

        <div>
          <h2 className="font-medium mb-2">Members</h2>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading ? (
            <div>Loading...</div>
          ) : (
            <table className="min-w-full border">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-2 text-left">User ID</th>
                  <th className="p-2 text-left">Role</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id} className="border-t">
                    <td className="p-2">{m.user_id}</td>
                    <td className="p-2">{m.role}</td>
                    <td className="p-2 text-right">
                      {isManager(role) ? (
                        <button className="text-red-600" onClick={() => removeMember(m.id)} disabled={loading}>Remove</button>
                      ) : (
                        <span className="text-xs text-gray-400">No actions</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsMembers;
